import { putNextDisclosureDeadline } from "@/apis/disclosure-deadline/route";
import { ApiError } from "@/apis/errors";
import type { DisclosureDeadline } from "@/apis/disclosure-deadline/types";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import queries from "@/queries";
import { popModal } from "@/stores/modal";
import { Button } from "@/toggle-ui-kit/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Calendar } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import {
  formatDotDate,
  isoDate,
  parseDotDate,
  parseIsoDate,
} from "./deadline-date";

/** 실패 사유. 서버가 사유를 주면 그대로, 아니면 상황으로 추정한다 */
export const deadlineChangeFailureReason = (error: unknown): string => {
  if (error instanceof ApiError) return error.message;
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === undefined)
      return "서버에 연결하지 못했습니다. 네트워크를 확인해주세요.";
    if (status === 401 || status === 403)
      return "로그인이 만료되었거나 권한이 없습니다. 다시 로그인해주세요.";
    if (status >= 500)
      return `서버 오류가 발생했습니다(${status}). 잠시 후 다시 시도해주세요.`;
    return `요청이 거부되었습니다(${status}).`;
  }
  return "알 수 없는 오류가 발생했습니다.";
};

export const DeadlineModal = ({ current }: { current: DisclosureDeadline }) => {
  const [text, setText] = useState(() => {
    const d = parseIsoDate(current.deadline);
    return d ? formatDotDate(d) : "";
  });
  const textRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLInputElement>(null);
  const parsed = parseDotDate(text);
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (deadline: Date) =>
      putNextDisclosureDeadline(isoDate(deadline)),
    onSuccess: (updated) => {
      // 응답이 새 마감일이라 바로 넣는다. invalidate 만 하면 refetch 끝날 때까지 옛 D-day 가 보인다
      queryClient.setQueryData(
        queries.nextDisclosureDeadline().queryKey,
        updated,
      );
      queryClient.invalidateQueries(queries.nextDisclosureDeadline());
      toast.success("공시 마감일이 변경되었습니다.");
      popModal();
    },
    onError: (error) =>
      toast.error("공시 마감일 변경에 실패했습니다.", {
        description: deadlineChangeFailureReason(error),
      }),
  });

  const handleSave = () => {
    if (parsed && !isPending) mutate(parsed);
  };

  // showPicker 는 Safari 16 미만에 없고, 사용자 조작 없이 부르면 NotAllowedError 를 던진다.
  // 열지 못하면 조용히 끝내지 않고 직접 입력할 수 있는 칸으로 포커스를 넘긴다
  const openPicker = () => {
    try {
      pickerRef.current?.showPicker();
    } catch {
      textRef.current?.focus();
    }
  };

  return (
    <ModalWrapper className="flex w-[458px] flex-col gap-0 rounded-lg border border-line-normal bg-white p-0 shadow-2xl">
      <div className="flex items-center px-6 pt-5">
        <h2 className="label-1-normal-semibold text-label-normal">
          공시 마감일 변경
        </h2>
      </div>

      <div className="flex flex-col gap-2 px-6 py-4">
        <p className="label-2-medium text-label-alternative">
          마감일을 선택해주세요
        </p>
        <div className="flex items-center gap-2 rounded-lg border border-line-normal bg-white px-3 py-2 focus-within:shadow-input-focus">
          <input
            ref={textRef}
            type="text"
            aria-label="공시 마감일"
            placeholder="YYYY.MM.DD"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            className="body-1-normal-regular min-w-0 flex-1 bg-transparent text-label-normal outline-none placeholder:text-label-assistive"
          />
          {/* ponytail: 달력은 브라우저 기본 date picker. 디자인 datepicker 가 확정되면 교체 */}
          {/* date input 은 버튼의 형제다 — 버튼 안에 넣으면 명세상 금지된 중첩이고 클릭 처리가 브라우저마다 갈린다 */}
          <div className="relative shrink-0">
            <button type="button" aria-label="달력 열기" onClick={openPicker}>
              <Calendar className="size-3.5 text-blue-500" />
            </button>
            <input
              ref={pickerRef}
              type="date"
              tabIndex={-1}
              aria-hidden
              value={parsed ? isoDate(parsed) : ""}
              onChange={(e) =>
                e.target.value && setText(e.target.value.replaceAll("-", "."))
              }
              className="pointer-events-none absolute inset-0 opacity-0"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 px-6 pb-5">
        <Button
          variant="outlined"
          size="base"
          onClick={popModal}
          disabled={isPending}
          className="min-w-[74px]"
        >
          취소
        </Button>
        <Button
          variant="solid"
          size="base"
          onClick={handleSave}
          disabled={!parsed || isPending}
          className="min-w-[74px]"
        >
          변경
        </Button>
      </div>
    </ModalWrapper>
  );
};
